"use client";

/**
 * The widgets' pick-one list: a button with a list under it, and a search box on top once the
 * list is long. Every widget that asks a visitor to pick one of several choices uses this.
 *
 * Not a native select (AGENTS.md section 6). A select's open list is drawn by the browser and
 * takes nothing of the site's theme, and a long list cannot be searched. The admin kit's
 * Combobox is not here either: this package renders on the tenant's public page without it.
 * So it is built here, after the ARIA select-only combobox pattern:
 *   - the button is the combobox, named by `label`, its text is the value;
 *   - Enter, Space, ArrowDown or ArrowUp open it; the arrows, Home and End move in the list,
 *     Enter picks, a letter jumps (type-ahead), Escape closes and gives focus back to the
 *     button, Tab closes and moves on;
 *   - a long list has a search box on top, which holds the focus while it is open (but not
 *     after a touch, where focus would pop the phone's keyboard over the list).
 *
 * The list is in the markup while closed (`hidden`), so the button's `aria-controls` always
 * points at an element, and a server render and the browser's first render print the same
 * choices. With no `onChange` (a JS-free render) it still prints the same markup.
 *
 * Its look is `COMBO_CSS` (`./css.ts`), which every widget that uses it prints with its own
 * sheet. A widget shapes the button like its other fields with `buttonClassName` and its own
 * rules; the list keeps one look everywhere, in the theme's tokens.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { PICK_SEARCH_FROM, matchChoices, typeAheadIndex, type PickChoice } from "./match.js";

function Svg({ className, children }: { className: string; children: ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function Combo({
  id,
  className,
  buttonClassName,
  buttonAttrs,
  label,
  value,
  choices,
  onChange,
  placeholder = "",
  disabled,
  invalid,
  searchLabel,
  emptyText,
  fitPanel,
  trigger,
  children,
}: {
  /** The button's id, so a visible `<label htmlFor>` points at it. */
  id?: string;
  /** Extra class on the root, for the place it sits in. */
  className?: string;
  /** Extra class on the button, so it can look like the widget's other fields. */
  buttonClassName?: string;
  /** Extra attributes on the button, such as a `data-*` mark a widget finds it by. */
  buttonAttrs?: Record<string, string>;
  /** The field's name: names the button and the list. */
  label: string;
  value: string;
  choices: readonly PickChoice[];
  /** Absent in a JS-free render: the markup is the same, nothing can be picked. */
  onChange?: (value: string) => void;
  /** The button's text while no choice carries `value`. */
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** The search box's name and grey text; the box shows on a list of `PICK_SEARCH_FROM` or more. */
  searchLabel?: string;
  /** The line under a search that kept nothing. */
  emptyText?: string;
  /** The list exactly as wide as the button, for a field in a narrow column. */
  fitPanel?: boolean;
  /** The button's content in place of the value and the arrow, for a button drawn as something else. */
  trigger?: ReactNode;
  /** More markup inside the root, after the list: the input a form posts the pick through (`ComboField`). */
  children?: ReactNode;
}) {
  const auto = useId();
  const buttonId = id ?? `${auto}-btn`;
  const listId = `${buttonId}-list`;
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [alignEnd, setAlignEnd] = useState(false);
  const byTouch = useRef(false);
  const typed = useRef({ text: "", at: 0 });

  const searchable = !!searchLabel && choices.length >= PICK_SEARCH_FROM;
  const shown = searchable ? matchChoices(choices, query) : [...choices];
  const selected = choices.find((c) => c.value === value);
  const optionId = (i: number) => `${listId}-${i}`;
  const activeId = open && active >= 0 && active < shown.length ? optionId(active) : undefined;

  const show = () => {
    setQuery("");
    setActive(Math.max(0, choices.findIndex((c) => c.value === value)));
    setOpen(true);
  };
  const hide = (refocus: boolean) => {
    setOpen(false);
    setQuery("");
    if (refocus) button.current?.focus();
  };
  const pick = (choice: PickChoice) => {
    if (choice.value !== value) onChange?.(choice.value);
    hide(true);
  };

  // The latest close, so the listeners below are added once per opening.
  const close = useRef(hide);
  close.current = hide;

  useEffect(() => {
    if (!open) return;
    const el = root.current;
    const onDown = (e: PointerEvent) => {
      if (el && !el.contains(e.target as Node)) close.current(false);
    };
    // Native and on the root, so it runs before a phone sheet or a dropdown around it hears
    // the same Escape and closes itself too.
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      close.current(true);
    };
    document.addEventListener("pointerdown", onDown);
    el?.addEventListener("keydown", onKey);
    if (searchable && !byTouch.current) search.current?.focus({ preventScroll: true });
    else list.current?.focus({ preventScroll: true });
    // Measured from the button's left edge and the panel's own width, which do not depend on
    // the side it is drawn on, so a second opening never flips it back.
    const panel = el?.querySelector<HTMLElement>(".wa-cmb-panel");
    const from = button.current?.getBoundingClientRect().left ?? 0;
    const room = document.documentElement.clientWidth;
    setAlignEnd(room > 0 && from + (panel?.offsetWidth ?? 0) > room - 8);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      el?.removeEventListener("keydown", onKey);
    };
  }, [open, searchable]);

  // Keep the active choice in view while the arrows walk past the list's edge.
  useEffect(() => {
    if (!open || active < 0) return;
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    byTouch.current = false;
    if (!open) show();
  };

  const onPanelKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = shown.length - 1;
    const inSearch = e.target === search.current;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((i) => Math.min(i + 1, last));
        return;
      case "ArrowUp":
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        return;
      case "Home":
      case "End":
        if (inSearch) return;
        e.preventDefault();
        setActive(e.key === "Home" ? 0 : last);
        return;
      case "Enter": {
        // Never the form's Enter: a pick inside a search form must not send it.
        e.preventDefault();
        const choice = shown[active];
        if (choice) pick(choice);
        return;
      }
      case "Tab":
        hide(false);
        return;
    }
    if (inSearch || e.key.length !== 1 || e.altKey || e.ctrlKey || e.metaKey) return;
    // Type-ahead: letters typed within a moment of each other read as one prefix.
    e.preventDefault();
    const now = Date.now();
    const text = now - typed.current.at < 700 ? typed.current.text + e.key : e.key;
    typed.current = { text, at: now };
    const i = typeAheadIndex(shown, active, text);
    if (i >= 0) setActive(i);
  };

  return (
    <div ref={root} className={className ? `wa-cmb ${className}` : "wa-cmb"} data-fit={fitPanel ? "" : undefined}>
      <button
        {...buttonAttrs}
        ref={button}
        id={buttonId}
        type="button"
        role="combobox"
        className={buttonClassName ? `wa-cmb-btn ${buttonClassName}` : "wa-cmb-btn"}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        data-empty={selected ? undefined : ""}
        onPointerDown={(e) => {
          byTouch.current = e.pointerType === "touch";
        }}
        onClick={() => (open ? hide(true) : show())}
        onKeyDown={onButtonKey}
      >
        {trigger ?? (
          <>
            <span className="wa-cmb-val">{selected?.label ?? placeholder}</span>
            <Svg className="wa-cmb-chev">
              <path d="m6 9 6 6 6-6" />
            </Svg>
          </>
        )}
      </button>
      <div className="wa-cmb-panel" hidden={!open} data-align={alignEnd ? "end" : undefined} onKeyDown={onPanelKey}>
        {searchable && (
          <span className="wa-cmb-search">
            <Svg className="wa-cmb-sicon">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </Svg>
            <input
              ref={search}
              type="text"
              aria-label={searchLabel}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={activeId}
              autoComplete="off"
              spellCheck={false}
              placeholder={searchLabel}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
            />
          </span>
        )}
        <ul
          ref={list}
          id={listId}
          className="wa-cmb-list"
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          aria-activedescendant={searchable ? undefined : activeId}
        >
          {shown.map((c, i) => (
            <li
              {...c.attrs}
              key={c.value}
              id={optionId(i)}
              className="wa-cmb-opt"
              role="option"
              aria-selected={c.value === value}
              data-i={i}
              data-value={c.value}
              data-active={open && i === active ? "" : undefined}
              onPointerMove={() => {
                if (i !== active) setActive(i);
              }}
              onClick={() => pick(c)}
            >
              <span>{c.label}</span>
              {c.value === value && (
                <Svg className="wa-cmb-tick">
                  <path d="M20 6 9 17l-5-5" />
                </Svg>
              )}
            </li>
          ))}
        </ul>
        {searchable && !shown.length && emptyText && <p className="wa-cmb-none">{emptyText}</p>}
      </div>
      {children}
    </div>
  );
}
